/**
 * Copyright (c) 2018-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author Adam Midlik <midlik@gmail.com>
 */
import { canonicalJsonString } from './json';
// TODO move to mol-math as Vector???
/** Get the maximum value in an array */
export function arrayMax(array) {
    var max = -Infinity;
    for (var i = 0, il = array.length; i < il; ++i) {
        if (array[i] > max)
            max = array[i];
    }
    return max;
}
/** Get the minimum value in an array */
export function arrayMin(array) {
    var min = Infinity;
    for (var i = 0, il = array.length; i < il; ++i) {
        if (array[i] < min)
            min = array[i];
    }
    return min;
}
/** Get the minimum & maximum value in an array */
export function arrayMinMax(array) {
    var min = Infinity;
    var max = -Infinity;
    for (var i = 0, il = array.length; i < il; ++i) {
        if (array[i] < min)
            min = array[i];
        if (array[i] > max)
            max = array[i];
    }
    return [min, max];
}
/** Get the sum of values in an array */
export function arraySum(array, stride, offset) {
    if (stride === void 0) { stride = 1; }
    if (offset === void 0) { offset = 0; }
    var n = array.length;
    var sum = 0;
    for (var i = offset; i < n; i += stride) {
        sum += array[i];
    }
    return sum;
}
/** Get the mean of values in an array */
export function arrayMean(array, stride, offset) {
    if (stride === void 0) { stride = 1; }
    if (offset === void 0) { offset = 0; }
    return arraySum(array, stride, offset) / (array.length / stride);
}
/** Get the root mean square of values in an array */
export function arrayRms(array) {
    var n = array.length;
    var sumSq = 0;
    for (var i = 0; i < n; ++i) {
        var di = array[i];
        sumSq += di * di;
    }
    return Math.sqrt(sumSq / n);
}
/** Fill an array with serial numbers starting from 0 until n - 1 (defaults to array.length) */
export function fillSerial(array, n) {
    for (var i = 0, il = n ? Math.min(n, array.length) : array.length; i < il; ++i)
        array[i] = i;
    return array;
}
export function arrayRemoveInPlace(xs, x) {
    var i = 0, found = false;
    for (var il = xs.length; i < il; i++) {
        if (xs[i] === x) {
            found = true;
            break;
        }
    }
    if (!found)
        return false;
    arrayRemoveAtInPlace(xs, i);
    return true;
}
export function arrayRemoveAtInPlace(xs, idx) {
    for (var i = idx, _i = xs.length - 1; i < _i; i++) {
        xs[i] = xs[i + 1];
    }
    xs.pop();
}
export function arraySetAdd(xs, x) {
    if (xs.indexOf(x) >= 0)
        return false;
    xs.push(x);
    return true;
}
export function arraySetRemove(xs, x) {
    var idx = xs.indexOf(x);
    if (idx < 0)
        return false;
    for (var i = idx, _i = xs.length - 1; i < _i; i++) {
        xs[i] = xs[i + 1];
    }
    xs.pop();
    return true;
}
/**
 * Caution, O(n^2) complexity. Only use for small input sizes.
 * For larger inputs consider using `SortedArray`.
 */
export function arrayAreIntersecting(xs, ys) {
    for (var i = 0, il = xs.length; i < il; ++i) {
        if (ys.includes(xs[i]))
            return true;
    }
    return false;
}
/**
 * Caution, O(n^2) complexity. Only use for small input sizes.
 * For larger inputs consider using `SortedArray`.
 */
export function arrayIntersectionSize(xs, ys) {
    var count = 0;
    for (var i = 0, il = xs.length; i < il; ++i) {
        if (ys.includes(xs[i]))
            count += 1;
    }
    return count;
}
export function arrayEqual(xs, ys) {
    if (!xs || xs.length === 0)
        return !ys || ys.length === 0;
    if (!ys)
        return false;
    var lenX = xs.length;
    if (lenX !== ys.length)
        return false;
    for (var i = 0; i < lenX; i++) {
        if (xs[i] !== ys[i])
            return false;
    }
    return true;
}
export function arrayIsIdentity(xs) {
    for (var i = 0, _i = xs.length; i < _i; i++) {
        if (xs[i] !== i)
            return false;
    }
    return true;
}
export function arrayMapUpsert(xs, key, value) {
    for (var i = 0, il = xs.length; i < il; ++i) {
        if (xs[i][0] === key) {
            xs[i][1] = value;
            return;
        }
    }
    xs.push([key, value]);
}
/** Return an array containing integers from [start, end) if `end` is given,
 * or from [0, start) if `end` is omitted. */
export function range(start, end) {
    if (end === undefined) {
        end = start;
        start = 0;
    }
    var length = Math.max(end - start, 0);
    var result = Array(length);
    for (var i = 0; i < length; i++) {
        result[i] = start + i;
    }
    return result;
}
/** Copy all elements from `src` to the end of `dst`.
 * Equivalent to `dst.push(...src)`, but avoids storing element on call stack. Faster that `extend` from Underscore.js.
 * `extend(a, a)` will double the array.
 * Returns the modified `dst` array.
 */
export function arrayExtend(dst, src) {
    var offset = dst.length;
    var nCopy = src.length;
    dst.length += nCopy;
    for (var i = 0; i < nCopy; i++) {
        dst[offset + i] = src[i];
    }
    return dst;
}
/** Check whether `array` is sorted, sort if not. */
export function sortIfNeeded(array, compareFn) {
    return arrayIsSorted(array, compareFn) ? array : array.sort(compareFn);
}
/** Decide whether `array` is sorted. */
export function arrayIsSorted(array, compareFn) {
    for (var i = 1, n = array.length; i < n; i++) {
        if (compareFn(array[i - 1], array[i]) > 0) {
            return false;
        }
    }
    return true;
}
/** Remove all elements from the array which do not fulfil `predicate`. Return the modified array itself. */
export function filterInPlace(array, predicate) {
    var n = array.length;
    var iDest = 0;
    for (var iSrc = 0; iSrc < n; iSrc++) {
        if (predicate(array[iSrc])) {
            array[iDest++] = array[iSrc];
        }
    }
    array.length = iDest;
    return array;
}
/** Return an array of all distinct values from `values`
 * (i.e. with removed duplicates).
 * Uses deep equality for objects and arrays,
 * independent from object key order and undefined properties.
 * E.g. {a: 1, b: undefined, c: {d: [], e: null}} is equal to {c: {e: null, d: []}}, a: 1}.
 * If two or more objects in `values` are equal, only the first of them will be in the result. */
export function arrayDistinct(values) {
    var seen = new Set();
    var result = [];
    for (var _a = 0, values_1 = values; _a < values_1.length; _a++) {
        var value = values_1[_a];
        var key = canonicalJsonString(value);
        if (!seen.has(key)) {
            seen.add(key);
            result.push(value);
        }
    }
    return result;
}
